/* RDG — Guide des tailles en sheet montant (refonte DA 2026-08, brief §7).
 * Lot 11 — 21/08/2026.
 *
 * Le sheet s'ouvre et se ferme SANS CE FICHIER : c'est le socle de tiroirs du
 * thème (hdt-drawer + aria-controls) qui fait le travail, et le balisage seul
 * suffit. Ce script n'ajoute que deux choses, inutiles au cas nominal et
 * décisives au cas dégradé. S'il ne se charge pas, on perd les deux et le
 * guide reste consultable.
 *
 *  1. LE FILET D'ÉTAT FINAL, DANS LES DEUX SENS.
 *     Le thème ouvre et ferme ses dialogues avec `await animation`, puis
 *     seulement ensuite retire la classe et appelle `dialog.close()`.
 *     L'animation passe par requestAnimationFrame, GELÉ dès que la page n'est
 *     pas rendue — onglet en arrière-plan, ce qui arrive très normalement sur
 *     un téléphone.
 *       · à l'ouverture, le panneau reste figé sur sa première image, donc
 *         TRANSLATÉ HORS DE L'ÉCRAN : sheet invisible ;
 *       · à la fermeture, `dialog.close()` n'est JAMAIS appelé : le panneau
 *         reste ouvert, modal, par-dessus le sheet de taille — plus rien n'est
 *         cliquable dans la page.
 *     Le second cas n'a rien de théorique : REPRODUIT le 21/08 en mesurant, sur
 *     un onglet Chrome passé en arrière-plan. `document.hidden === true`,
 *     `dialog-closing` encore là 5,6 s après le clic sur la croix, `open`
 *     toujours vrai. C'est le défaut le plus grave des deux.
 *
 *     Le déclencheur est un MutationObserver sur la classe du dialogue, et non
 *     un écouteur de clic : la fermeture a QUATRE chemins (croix, Escape,
 *     glissement vers le bas, clic à l'extérieur) et trois d'entre eux ne
 *     passent par aucun bouton. Observer l'état couvre les quatre d'un coup.
 *     setTimeout, et non rAF, précisément parce que c'est rAF qui peut être
 *     gelé — un onglet caché le rallonge à ~1 s, ce qui reste borné.
 *
 *  2. LE PIÈGE DE FOCUS RENDU AU SHEET DE TAILLE. Le guide s'ouvre le plus
 *     souvent PAR-DESSUS le sheet de sélection de taille (c'est son seul
 *     chemin en mobile, où le lien de la fiche produit est masqué). En se
 *     fermant, `DialogComponent.close()` appelle `removeTrapFocus()`, qui est
 *     global : le sheet resté ouvert dessous se retrouve sans piège de focus,
 *     et la tabulation part se promener dans la page derrière. On le lui rend.
 *
 * CE QUI N'EST PAS ICI, ET POURQUOI — la durée du brief.
 * `Drawer` ouvre en 500 ms et ferme en 400 ms ; le brief §2.4 fixe
 * --dur-sheet à 320 ms. La surcharge a été écrite, poussée, puis RETIRÉE :
 * elle avait besoin de `animate` de Motion, et un `import('@theme/m')` depuis
 * un script CLASSIQUE ne marche pas sur ce thème. Shopify passe les assets JS
 * dans esbuild, qui réécrit l'import dynamique en
 * `Promise.resolve().then(() => __toESM(require('@theme/m')))` : `require`
 * n'existe pas dans un navigateur, la promesse est rejetée, et un `.catch()`
 * l'avale sans un mot dans la console. Vérifié en lisant le fichier RÉELLEMENT
 * SERVI, pas la source locale — les deux diffèrent (7 Ko ici, 2,8 Ko servis).
 * Le seul chemin qui tienne est un `<script type="module">` avec un import
 * statique, comme rdg-motion.js — mais alors tout le fichier disparaît si
 * l'import échoue, y compris le filet n°1, qui vaut bien plus que 180 ms.
 * La bonne place pour cette durée est la surcharge de `Drawer.prototype` déjà
 * posée dans rdg-motion.js, qui filtre sur l'identifiant du dialogue : il n'y
 * a qu'un identifiant à y ajouter. Fichier d'une autre session, donc proposé
 * et non pris.
 */
(function () {
  'use strict';
  if (window.__rdgGuideSheet) return;
  window.__rdgGuideSheet = true;

  var SHEET_ID = 'RdgGuideSheet';
  var SIZE_SHEET_ID = 'RdgSizeSheet';

  /* Plafond volontairement large : il doit couvrir la durée annoncée du geste
     (500 ms à l'ouverture, 400 à la fermeture) plus la marge d'un appareil
     lent, sans jamais interrompre une animation qui se déroule normalement. */
  var DUREE_MAX = 900;

  function sheet() { return document.getElementById(SHEET_ID); }

  /* ---------------------------------------------------------------------- */
  /* 1. Filet d'état final                                                   */
  /* ---------------------------------------------------------------------- */

  function terminerLesAnimations(dlg) {
    if (typeof dlg.getAnimations === 'function') {
      dlg.getAnimations().forEach(function (a) {
        try { a.finish(); } catch (e) { /* déjà terminée, ou sans état final */ }
      });
    }
  }

  function finaliserOuverture() {
    var dlg = sheet();
    if (!dlg || !dlg.classList.contains('dialog-opening')) return;
    terminerLesAnimations(dlg);
    /* Motion n'utilise pas toujours l'API d'animations du navigateur : quand
       il retombe sur son propre moteur, il écrit un transform en style en
       ligne, que getAnimations() ne voit pas. */
    dlg.style.removeProperty('transform');
    dlg.classList.remove('dialog-opening');
  }

  function finaliserFermeture() {
    var dlg = sheet();
    if (!dlg || !dlg.classList.contains('dialog-closing')) return;
    terminerLesAnimations(dlg);
    if (dlg.open) {
      try { dlg.close(); } catch (e) { /* déjà fermé entre-temps */ }
    }
    dlg.classList.remove('dialog-closing');
    dlg.style.removeProperty('transform');

    /* Le thème libère le défilement de la page à la fin de SA fermeture, donc
       jamais si l'animation ne se résout pas. On ne le fait que s'il ne reste
       aucun dialogue ouvert — le sheet de taille est souvent encore là
       dessous, et lui doit garder le verrou. L'opération est idempotente : si
       le thème repasse plus tard, il ne fera rien de plus. */
    if (!document.querySelector('dialog[open]')) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /* On observe la CLASSE plutôt qu'un clic : la fermeture a quatre chemins
     (croix, Escape, glissement, clic extérieur) et trois ne passent par aucun
     bouton. Le thème pose `dialog-opening` / `dialog-closing` sur les quatre. */
  function surveillerLEtat(dlg) {
    var minuteur = null;
    var obs = new MutationObserver(function () {
      var enCours = dlg.classList.contains('dialog-opening') || dlg.classList.contains('dialog-closing');
      if (!enCours) return;
      if (minuteur) clearTimeout(minuteur);
      minuteur = setTimeout(function () {
        minuteur = null;
        finaliserOuverture();
        finaliserFermeture();
      }, DUREE_MAX);
    });
    obs.observe(dlg, { attributes: true, attributeFilter: ['class'] });
  }

  /* ---------------------------------------------------------------------- */
  /* 2. Rendre le piège de focus au sheet de taille                          */
  /* ---------------------------------------------------------------------- */

  /* `close` est l'événement natif de <dialog> : il part quel que soit le
     chemin de fermeture, y compris quand c'est le filet ci-dessus qui ferme. */
  function surveillerLeFocus(dlg) {
    dlg.addEventListener('close', function () {
      var taille = document.getElementById(SIZE_SHEET_ID);
      if (!taille || !taille.open) return;
      var hote = taille.parentElement;
      if (hote && typeof hote.trapFocus === 'function') {
        try { hote.trapFocus(); } catch (e) { /* socle absent : on n'insiste pas */ }
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Branchement                                                             */
  /* ---------------------------------------------------------------------- */

  function demarrer() {
    var dlg = sheet();
    if (!dlg) return;
    surveillerLEtat(dlg);
    surveillerLeFocus(dlg);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
